<template>
  <div class="geofencing-view">
    <Tabs>
      <template #tab-selector>
        <button
          :class="{active: tabContent === TAB_GEOFENCING }"
          class="tab-link"
          @click="setTab(TAB_GEOFENCING)"
        >
          Geofencing
        </button>
        <button
          :class="{active: tabContent === TAB_GEOFENCING_EVENTS }"
          class="tab-link"
          @click="setTab(TAB_GEOFENCING_EVENTS)"
        >
          Zdarzenia
        </button>
      </template>
      <template #tab-content>
        <div
          v-if="tabContent === TAB_GEOFENCING"
          class="row ms-0 me-0 p-0 geofencing-layout"
        >
          <div class="col-md-4 pt-4 position-relative geofencing-position-list-section">
            <h4 class="float-left">
              {{ $t('geofencing.itemsTitle') }}
            </h4>
            <i
              @click="toggleCrud"
              class="bi bi-plus btn btn-primary btn-sm float-right fs-2 ps-2 pe-2 pt-0 pb-0"
            />
            <p
              class="mt-15"
              v-if="items.length === 0"
            >
              {{ $t('geofencing.itemsEmpty') }}
            </p>
            <table
              v-if="items.length > 0"
              class="table table-bordered mt-15"
            >
              <tbody>
                <tr
                  :key="item.id"
                  v-for="item in items"
                  @click="viewGeometry(item)"
                  class="cursor-pointer"
                >
                  <td class="ps-2 pe-2">
                    {{ item.name }}
                  </td>
                  <td
                    class="w-20px ps-3 pe-3 text-center"
                    @click="viewItem(item)"
                  >
                    <i class="bi bi-pencil-square cursor-pointer" />
                  </td>
                </tr>
              </tbody>
            </table>

            <transition
              name="fade"
              mode="out-in"
            >
              <div
                v-if="isOpen"
                key="element"
                class="col-12 position-absolute top-0 h-100 pe-4 m-geofencing"
              >
                <form @submit.prevent="submitForm">
                  <h4 class="m-geofencing__title">
                    {{ isEdit ? $t('geofencing.editTitle') : $t('geofencing.addTitle') }}
                  </h4>
                  <button
                    @click="toggleCrud"
                    class="m-geofencing__close close"
                    type="button"
                  >
                    <i class="fas fa-times" />
                  </button>
                  <div class="m-geofencing__content">
                    <label
                      class="control-label"
                      for="geofencing-name"
                    >{{ $t('geofencing.fieldName') }}</label>
                    <input
                      class="form-control mb-5"
                      id="geofencing-name"
                      type="text"
                      v-model="formData.name"
                      @input="validate"
                    >
                    <div
                      v-if="validationError"
                      class="vuelidate-errors"
                    >
                      <p class="error-msg">
                        {{ $t('geofencing.errorMessage') }}
                      </p>
                    </div>
                    <button class="btn btn-primary">
                      <i class="fa fa-save" />{{ $t('components.vehicles.modals.save') }}
                    </button>
                    <a
                      v-if="isEdit"
                      class="btn btn-danger float-right"
                      @click="removeItem(id)"
                    >
                      <i class="fa fa-remove" />{{ $t('components.sentGeo.dataGrids.delete') }}
                    </a>
                  </div>
                </form>
              </div>
            </transition>
          </div>
          <div
            class="col-md-8 pe-0 position-relative geofencing-map-section"
            id="geofencing-map-section"
          >
            <div
              ref="geofencingMap"
              class="geofencing-map"
              id="geofencing-map"
            />
            <div
              v-if="drawControlsMenu"
              class="geofencing-toolbar"
            >
              <button
                type="button"
                :class="{active: drawMode === 'polygon'}"
                @click="startDraw('polygon')"
              >
                <i class="bi bi-pentagon" />
              </button>
              <button
                type="button"
                :class="{active: drawMode === 'rectangle'}"
                @click="startDraw('rectangle')"
              >
                <i class="bi bi-square" />
              </button>
              <button
                type="button"
                :class="{active: drawMode === 'circle'}"
                @click="startDraw('circle')"
              >
                <i class="bi bi-circle" />
              </button>
              <button
                type="button"
                class="geofencing-toolbar__delete"
                @click="deleteDrawn"
              >
                <i class="bi bi-trash" />
              </button>
            </div>
          </div>
        </div>
        <div
          v-if="tabContent === TAB_GEOFENCING_EVENTS"
          class="row ms-0 me-0 geofencing-events-layout"
        >
          <div class="col-md-12 pt-4 position-relative">
            <GeoFencingEvents :items="items" />
          </div>
        </div>
      </template>
    </Tabs>
  </div>
</template>

<script lang="ts">
import 'maplibre-gl/dist/maplibre-gl.css';
import maplibregl from 'maplibre-gl';
import {TerraDraw, TerraDrawCircleMode, TerraDrawPolygonMode, TerraDrawRectangleMode, TerraDrawSelectMode} from 'terra-draw';
import {TerraDrawMapLibreGLAdapter} from 'terra-draw-maplibre-gl-adapter';
import {defineComponent, markRaw, nextTick} from 'vue';
import {getCompanyFromJwt, getParameterFromToken} from '@/crud/getters';
import {getAllGeoFencings} from '@/crud/geofencing/getters';
import {addGeofencing, editGeofencing} from '@/crud/geofencing/senders';
import {removeGeofencing} from '@/crud/geofencing/removers';
import Tabs from '@/components/extras/Tabs.vue';
import GeoFencingEvents from '@/components/content/GeoFencingEvents.vue';
import {createMapStyle, MapProvider, resolveProvider} from '@/services/map/map-style';
import {boundsFromGeofenceGeometry, circleToPolygon, polygonToCircle} from '@/services/map/geofence-geometry';

const TAB_GEOFENCING = 0
const TAB_GEOFENCING_EVENTS = 1

// Map center in MapLibre order [lng, lat] (geographic centre of Poland).
const DEFAULT_CENTER: [number, number] = [19.1451, 51.9194]

export default defineComponent({
  name: 'GeofencingMap',
  components: {Tabs, GeoFencingEvents},
  data() {
    return {
      zoom: 7,
      minZoom: 3,
      maxZoom: 18,
      map: null as maplibregl.Map | null,
      draw: null as TerraDraw | null,
      drawMode: null as string | null,
      geometry: [] as any,
      items: [],
      drawControlsMenu: false,
      validationError: false,
      isOpen: false,
      isEdit: false,
      id: null,
      formData: {
        name: '',
      },
      tabContent: 0,
      TAB_GEOFENCING: TAB_GEOFENCING,
      TAB_GEOFENCING_EVENTS: TAB_GEOFENCING_EVENTS
    }
  },
  methods: {
    setTab(tab: number): void {
      this.tabContent = tab
      this.teardownMap()

      if (tab === this.TAB_GEOFENCING) {
        nextTick(() => this.loadMap())
      }
    },
    loadMap() {
      const self: any = this
      getParameterFromToken('MAP_TYPE')
          .then((res: string) => {
            self.createMap(resolveProvider(res))
          })
          .catch(() => {
            // No MAP_TYPE claim in the token -> fall back to the default base map (OpenFreeMap).
            self.createMap('OFM')
          })
    },
    async createMap(provider: MapProvider) {
      const self: any = this
      const mapContainer = self.$refs.geofencingMap as HTMLElement | undefined

      if (!mapContainer) {
        return
      }

      const style = await createMapStyle(provider)

      self.map = markRaw(new maplibregl.Map({
        container: mapContainer,
        style,
        center: DEFAULT_CENTER,
        zoom: self.zoom,
        minZoom: self.minZoom,
        maxZoom: self.maxZoom
      }))

      self.map.addControl(new maplibregl.NavigationControl(), 'top-left')

      self.map.on('load', () => {
        self.initDraw()
      })
    },
    initDraw() {
      const self: any = this

      const draw = new TerraDraw({
        adapter: new TerraDrawMapLibreGLAdapter({map: self.map}),
        modes: [
          new TerraDrawPolygonMode(),
          new TerraDrawRectangleMode(),
          new TerraDrawCircleMode(),
          // Polygons/rectangles are fully editable; circles can only be moved so they
          // stay circular and remain serialisable as { radius, center }.
          new TerraDrawSelectMode({
            flags: {
              polygon: {feature: {draggable: true, coordinates: {midpoints: true, draggable: true, deletable: true}}},
              rectangle: {feature: {draggable: true, coordinates: {draggable: true, deletable: false}}},
              circle: {feature: {draggable: true}}
            }
          })
        ]
      })

      self.draw = markRaw(draw)
      draw.start()
      draw.setMode('select')

      draw.on('finish', () => {
        self.captureGeometry()
        draw.setMode('select')
        self.drawMode = 'select'
      })

      draw.on('change', () => {
        if (draw.getMode() === 'select') {
          self.captureGeometry()
        }
      })
    },
    async startDraw(mode: string) {
      const self: any = this
      if (!self.draw) return

      if (self.draw.getSnapshot().length > 0) {
        const result = await self.$swal.fire({
          icon: 'warning',
          title: self.$t('geofencing.replaceDrawnAreaTitle'),
          text: self.$t('geofencing.replaceDrawnAreaMessage'),
          showCancelButton: true,
          confirmButtonText: self.$t('geofencing.replaceDrawnAreaConfirm'),
          cancelButtonText: self.$t('geofencing.cancel'),
        })

        if (!result.isConfirmed) {
          self.draw.setMode('select')
          self.drawMode = 'select'
          return
        }
      }

      self.draw.clear()
      self.draw.setMode(mode)
      self.drawMode = mode
    },
    deleteDrawn() {
      const self: any = this
      if (!self.draw) return

      self.draw.clear()
      self.draw.setMode('select')
      self.drawMode = 'select'
      self.geometry = []
    },
    captureGeometry() {
      const self: any = this
      if (!self.draw || !self.map) return

      const snapshot = self.draw.getSnapshot()
      if (!snapshot.length) {
        self.geometry = []
        return
      }

      const feature = snapshot[0]
      const coordinates = feature.properties.mode === 'circle'
          ? polygonToCircle(feature.geometry)
          : feature.geometry

      self.validationError = false

      const center = self.map.getCenter()
      self.geometry = JSON.stringify([{
        center: {lat: center.lat, lng: center.lng},
        zoom: self.map.getZoom(),
        minZoom: self.map.getMinZoom(),
        maxZoom: self.map.getMaxZoom(),
        geometry: coordinates
      }])
    },
    clearGeoJSONLayers() {
      const self: any = this
      self.draw?.clear()
      self.geometry = []
    },
    viewGeometry(item: { coordinations: any }) {
      const self: any = this
      if (!self.draw || !self.map) return

      self.clearGeoJSONLayers()

      const coordinations = item.coordinations
      if (!coordinations || coordinations.length === 0) return

      const geometryItem = coordinations[0]

      self.map.setMinZoom(geometryItem.minZoom)
      self.map.setMaxZoom(geometryItem.maxZoom)

      const geometry = geometryItem.geometry.radius
          ? circleToPolygon(geometryItem.geometry.center, geometryItem.geometry.radius)
          : geometryItem.geometry

      self.draw.addFeatures([{
        id: self.draw.getFeatureId(),
        type: 'Feature',
        properties: {mode: 'polygon'},
        geometry
      }])

      self.draw.setMode('select')
      self.drawMode = 'select'

      const bounds = boundsFromGeofenceGeometry(geometryItem.geometry)
      if (bounds) {
        self.map.fitBounds(bounds, {
          padding: 48,
          maxZoom: self.maxZoom,
          duration: 300
        })
      } else {
        self.map.jumpTo({center: [geometryItem.center.lng, geometryItem.center.lat], zoom: geometryItem.zoom})
      }
    },
    viewItem(item) {
      const self: any = this

      self.isEdit = true
      self.formData.name = item.name
      self.id = item.id
      self.toggleCrud()

      return true
    },
    validate() {
      const self: any = this

      self.validationError = self.formData.name.trim() === '' || self.geometry.length === 0
      return !self.validationError
    },
    clearForm() {
      const self: any = this

      self.isOpen = false
      self.formData.name = ''
      self.drawControlsMenu = false
      self.validationError = false
      self.id = null
      self.clearGeoJSONLayers()
    },
    removeItem(id: number) {
      const self: any = this

      self.$swal.fire({
        title: self.$t('geofencing.removeQuestion'),
        showDenyButton: false,
        showCancelButton: true,
        confirmButtonColor: '#d63043',
        confirmButtonText: self.$t('components.sentGeo.dataGrids.delete'),
        cancelButtonText: self.$t('geofencing.cancel'),
      }).then((result) => {

        if (result.isConfirmed) {
          removeGeofencing(id).then(() => {
            self.clearForm()
            self.getGeofencing()
            self.$toast.success(self.$t('geofencing.removed'))
          }).catch(() => {
            self.$toast.warning('ERROR !')
          })
        }
      })

    },
    submitForm() {
      const self: any = this

      if (!this.validate()) {
        return false
      }

      const postData = {
        companyId: getCompanyFromJwt(),
        name: self.formData.name,
        coordinations: self.geometry
      }

      if (self.id) {
        postData['id'] = self.id
      }

      if (self.isEdit) {
        editGeofencing(postData).then(() => {
          self.getGeofencing()
          self.clearForm()
          self.$toast.success(self.$t('geofencing.edited'))
        }).catch(() => {
          self.$toast.warning('ERROR !')
        })
      } else {
        addGeofencing(postData).then(() => {
          self.getGeofencing()
          self.clearForm()
          self.$toast.success(self.$t('geofencing.added'))
        }).catch(() => {
          self.$toast.warning('ERROR !')
        })
      }
    },
    toggleCrud() {
      const self: any = this
      self.isOpen = !self.isOpen

      if (!self.isOpen) {
        self.isEdit = false
        self.formData.name = ''
        self.geometry = []
        self.clearGeoJSONLayers()
      }

      if (self.isOpen && !self.isEdit) {
        self.clearGeoJSONLayers()
      }

      self.drawControlsMenu = !self.drawControlsMenu

      if (self.drawControlsMenu && self.draw) {
        self.draw.setMode('select')
        self.drawMode = 'select'
      }
    },
    getGeofencing() {
      const self: any = this
      self.items = []

      getAllGeoFencings().then((response: any) => {
        response.data.forEach((obj) => {
          self.items.push(obj)
        })
      })
    },
    teardownMap() {
      const self: any = this
      if (self.draw) {
        try {
          self.draw.stop()
        } catch (e) {
          // ignore cleanup errors
        }
        self.draw = null
      }
      if (self.map) {
        try {
          self.map.remove()
        } catch (e) {
          // ignore cleanup errors
        }
        self.map = null
      }
    }
  },
  beforeUnmount() {
    this.teardownMap()
  },
  mounted() {
    const self: any = this

    self.getGeofencing()
    self.loadMap()
  }
})
</script>
<style lang="scss" src="@/assets/scss/geofencing-map.scss"></style>
